import {
  DataInput,
  ExecutionType,
  isProvidedCodeSourceMode,
  StepType,
  TransformCodeSourceMode,
} from '@/types/automationFields.ts';
import { useCallback, useMemo } from 'react';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import MultiDataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiDataInputSelectorUI.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import {
  getCodeVariableNameErrorMessage,
  hasCodeInputBindingIssues,
  shouldShowCodeInputBindingFields,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/codeInputBindings.ts';
import {
  getDecisionCodeSourceMode,
  getDecisionExecutionType,
} from '@/utils/decisionControlLogic.ts';

type props = {
  step: AutomationStep & { data_inputs: DataInput[] };
  label?: string;
  description?: string;
  dataInputs?: DataInput[];
  onDataInputsChange?: (dataInputs: DataInput[]) => void;
};

type Fields = {
  data_inputs: DataInput[];
};

const MultiDataInputSelector = ({
  step,
  label,
  description,
  dataInputs: controlledDataInputs,
  onDataInputsChange,
}: props) => {
  const { fields, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });
  const codeInputNamesRequired =
    step.step_type === StepType.TransformData
      ? step.execution_type === ExecutionType.Code
      : step.step_type === StepType.Validate
        ? getDecisionExecutionType(step.control_logic) === ExecutionType.Code
        : false;
  const codeSourceMode =
    step.step_type === StepType.TransformData
      ? (step.code_source_mode ?? TransformCodeSourceMode.GenerateAlways)
      : step.step_type === StepType.Validate
        ? getDecisionCodeSourceMode(step.control_logic)
        : TransformCodeSourceMode.GenerateAlways;
  const isProvidedCode = isProvidedCodeSourceMode(codeSourceMode);

  const updateDataInputs = useCallback(
    (dataInputs: DataInput[]) => onChangeNoDelay({ data_inputs: dataInputs }),
    [onChangeNoDelay],
  );
  const dataInputs = controlledDataInputs ?? fields.data_inputs;
  const handleDataInputsChange = onDataInputsChange ?? updateDataInputs;

  const dataInputError = useStepSpecificError(step, StepErrorType.InvalidDataInput);
  const codeInputBindingError = useStepSpecificError(step, StepErrorType.InvalidCodeInputBinding);
  const hasCodeInputBindingErrors = step.errors.some(
    (error) => error.type === StepErrorType.InvalidCodeInputBinding,
  );
  const hasVisibleBindingIssue =
    hasCodeInputBindingErrors || hasCodeInputBindingIssues(dataInputs);

  const extractNoDataInputs = useStepSpecificError(step, StepErrorType.ExtractNoDataInputs);
  const codeInputNameErrorsByIndex = useMemo(() => {
    return Object.fromEntries(
      step.errors
        .filter(
          (error) => error.type === StepErrorType.InvalidCodeInputBinding && error.index !== null,
        )
        .map((error) => [error.index as number, getCodeVariableNameErrorMessage(error.message)]),
    );
  }, [step.errors]);

  return (
    <MultiDataInputSelectorUI
      dataInputs={dataInputs}
      stepId={step.step_id}
      updateDataInputs={handleDataInputsChange}
      label={label}
      description={description}
      error={extractNoDataInputs || dataInputError || codeInputBindingError}
      requireCodeInputNames={codeInputNamesRequired}
      showCodeInputNameFields={
        step.step_type === StepType.TransformData
          ? hasVisibleBindingIssue && !isProvidedCode
          : shouldShowCodeInputBindingFields({
              requireCodeInputNames: codeInputNamesRequired,
              isProvidedCode,
              hasBindingIssues: hasVisibleBindingIssue,
            })
      }
      expandCodeInputNamesOnInputAdd={isProvidedCode}
      codeInputNameErrorsByIndex={codeInputNameErrorsByIndex}
    />
  );
};

export default MultiDataInputSelector;
